interface EnvironmentConfig {
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId?: string;
  };
  api: {
    openWeatherMapApiKey?: string;
    noaaApiKey?: string;
  };
  stripe: {
    publishableKey: string;
  };
  features: {
    enableAnalytics: boolean;
    enableCrashReporting: boolean;
    debugMode: boolean;
  };
}

class EnvironmentValidator {
  private static requiredEnvVars = [
    'EXPO_PUBLIC_FIREBASE_API_KEY',
    'EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN',
    'EXPO_PUBLIC_FIREBASE_PROJECT_ID',
    'EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET',
    'EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID',
    'EXPO_PUBLIC_FIREBASE_APP_ID',
    'EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY',
  ];

  private static optionalEnvVars = [
    'EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID',
    'EXPO_PUBLIC_NOAA_API_KEY',
    'EXPO_PUBLIC_OPENWEATHERMAP_API_KEY',
  ];

  static validate(): void {
    console.log('[EnvironmentValidator] Checking environment variables...');

    // Direct checks (works with webpack DefinePlugin string replacement)
    const envVarsPresent = {
      EXPO_PUBLIC_FIREBASE_API_KEY: process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
      EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
      EXPO_PUBLIC_FIREBASE_PROJECT_ID: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
      EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
      EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
      EXPO_PUBLIC_FIREBASE_APP_ID: process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
      EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    };

    // Log all environment variables (redact sensitive values)
    console.log('[EnvironmentValidator] Environment vars present:', {
      EXPO_PUBLIC_FIREBASE_API_KEY: envVarsPresent.EXPO_PUBLIC_FIREBASE_API_KEY ? '***' : 'missing',
      EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN: envVarsPresent.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN || 'missing',
      EXPO_PUBLIC_FIREBASE_PROJECT_ID: envVarsPresent.EXPO_PUBLIC_FIREBASE_PROJECT_ID || 'missing',
      EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET: envVarsPresent.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET || 'missing',
      EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: envVarsPresent.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID ? '***' : 'missing',
      EXPO_PUBLIC_FIREBASE_APP_ID: envVarsPresent.EXPO_PUBLIC_FIREBASE_APP_ID ? '***' : 'missing',
      EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY: envVarsPresent.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY ? '***' : 'missing',
      EXPO_PUBLIC_OPENWEATHERMAP_API_KEY: process.env.EXPO_PUBLIC_OPENWEATHERMAP_API_KEY ? '***' : 'missing',
    });

    // Check for missing vars using direct property access (not dynamic)
    const missingVars: string[] = [];
    if (!envVarsPresent.EXPO_PUBLIC_FIREBASE_API_KEY) missingVars.push('EXPO_PUBLIC_FIREBASE_API_KEY');
    if (!envVarsPresent.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN) missingVars.push('EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN');
    if (!envVarsPresent.EXPO_PUBLIC_FIREBASE_PROJECT_ID) missingVars.push('EXPO_PUBLIC_FIREBASE_PROJECT_ID');
    if (!envVarsPresent.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET) missingVars.push('EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET');
    if (!envVarsPresent.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID) missingVars.push('EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID');
    if (!envVarsPresent.EXPO_PUBLIC_FIREBASE_APP_ID) missingVars.push('EXPO_PUBLIC_FIREBASE_APP_ID');
    if (!envVarsPresent.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY) missingVars.push('EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY');

    if (missingVars.length > 0) {
      const error = new Error(
        `Missing required environment variables: ${missingVars.join(', ')}\n` +
          'Please check your .env file or environment configuration.'
      );
      console.error('[EnvironmentValidator] Validation failed:', error.message);

      // In production, don't throw - just log the error
      if (!__DEV__) {
        console.error('[EnvironmentValidator] Running in production with missing vars - app may not function correctly');
        return;
      }

      throw error;
    }

    console.log('[EnvironmentValidator] All required environment variables found');
  }

  static getConfig(): EnvironmentConfig {
    try {
      this.validate();

      const config = {
        firebase: {
          apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY!,
          authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN!,
          projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID!,
          storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET!,
          messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID!,
          appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID!,
          measurementId: process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID,
        },
        api: {
          openWeatherMapApiKey: process.env.EXPO_PUBLIC_OPENWEATHERMAP_API_KEY || undefined,
          noaaApiKey: process.env.EXPO_PUBLIC_NOAA_API_KEY,
        },
        stripe: {
          publishableKey: process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY!,
        },
        features: {
          enableAnalytics: process.env.NODE_ENV === 'production',
          enableCrashReporting: process.env.NODE_ENV === 'production',
          debugMode: __DEV__ ?? process.env.NODE_ENV !== 'production',
        },
      };
      
      console.log('[EnvironmentValidator] Configuration loaded successfully');
      return config;
    } catch (error) {
      console.error('[EnvironmentValidator] Failed to load configuration:', error);
      throw error;
    }
  }
}

// In production, use hardcoded config as fallback
let env: EnvironmentConfig;

if (!__DEV__) {
  console.log('[Environment] Using production configuration');
  // Import production config directly to avoid env var issues
  const prodConfig = require('./env.production');
  env = prodConfig.env;
} else {
  env = EnvironmentValidator.getConfig();
}

export { env };
export type { EnvironmentConfig };
